/**
 * @packageDocumentation
 * @category Component
 */

import type { SelectOption } from 'BomdisoftComponents/inputs/field/select/render';
import { Children, Fragment, isValidElement, type ReactNode } from 'react';

type OptionProps = {
  children?: ReactNode;
  'data-description'?: string;
  'data-trigger-label'?: string;
  disabled?: boolean;
  label?: string;
  value?: string | number;
};

/* An option's children are often several nodes rather than one string: the phone
   input writes `{flag} {code}`, which arrives as an array. Flattening it is what
   keeps the flag in the label instead of silently falling back to the value. */
const toText = (node: ReactNode): string => {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(toText).join('');
  return '';
};

const toLabel = (props: OptionProps, value: string): string => {
  const fromChildren = toText(props.children).trim();
  if (fromChildren) return fromChildren;
  if (typeof props.label === 'string') return props.label;
  return value;
};

/**
 * Flattens a `Select`'s `<option>` children into rows a menu can render.
 *
 * An `<optgroup>` is flattened rather than skipped. No call site uses one today,
 * but silently dropping its options would lose data rather than leave a feature
 * unbuilt.
 */
export const readOptions = (children: ReactNode, group?: string): SelectOption[] =>
  Children.toArray(children).flatMap(child => {
    if (!isValidElement<OptionProps>(child)) return [];
    // Children.toArray does not flatten a fragment, and wrapping options in one
    // is ordinary enough that dropping them would look like the Select ignoring
    // its own children.
    if (child.type === Fragment) return readOptions(child.props.children, group);
    if (child.type === 'optgroup') return readOptions(child.props.children, child.props.label);
    if (child.type !== 'option') return [];
    const value = child.props.value === undefined ? '' : String(child.props.value);
    const description = child.props['data-description'];
    const triggerLabel = child.props['data-trigger-label'];
    return [{ ...(description ? { description } : {}), ...(triggerLabel ? { triggerLabel } : {}), disabled: Boolean(child.props.disabled), group, label: toLabel(child.props, value), value }];
  });
